// Minimal typed fetch wrapper used by all TanStack Query hooks.
//
// The SPA and the API share the same origin in production (FastAPI mounts
// the built bundle via StaticFiles), so we use relative paths everywhere.
// In dev, vite.config.ts proxies these paths to the local FastAPI server.

import type {
  AlertFilters,
  ApiKeyCreatedOut,
  ApiKeyOut,
  AuthUser,
  CompanyOut,
  CompanySummaryOut,
  CountyImpactStat,
  EmployerStat,
  FamilyMemberOut,
  RepeatRiskOut,
  ForecastOut,
  Outlook,
  IndicatorDataset,
  IndicatorStat,
  IndustryScorecard,
  IndustryStat,
  MergedMemberOut,
  MergeResult,
  MergeSuggestionOut,
  MonthStat,
  NoticeOut,
  OccupationMixOut,
  Page,
  ParentGroupStat,
  RadarNoticeOut,
  PeriodStat,
  ScraperRunOut,
  SearchResults,
  StateStat,
  StateStatusOut,
  SubscriptionOut,
  UsageOut,
} from "./types";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const filtered = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== "",
  );
  if (filtered.length === 0) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of filtered) sp.set(k, String(v));
  return "?" + sp.toString();
}

async function get<T>(path: string): Promise<T> {
  const resp = await fetch(path, { headers: { Accept: "application/json" } });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new ApiError(resp.status, text || resp.statusText);
  }
  return (await resp.json()) as T;
}

/** Like get(), but for endpoints that return plain text (e.g. markdown). */
async function getText(path: string): Promise<string> {
  const resp = await fetch(path);
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new ApiError(resp.status, text || resp.statusText);
  }
  return await resp.text();
}

async function del<T>(path: string): Promise<T> {
  const resp = await fetch(path, {
    method: "DELETE",
    headers: { Accept: "application/json" },
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new ApiError(resp.status, text || resp.statusText);
  }
  return (await resp.json()) as T;
}

// Same-origin fetch sends the session cookie by default; no credentials flag needed.
async function post<T>(path: string, body?: unknown): Promise<T> {
  return send<T>("POST", path, body);
}

async function put<T>(path: string, body?: unknown): Promise<T> {
  return send<T>("PUT", path, body);
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const resp = await fetch(path, {
    method,
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new ApiError(resp.status, text || resp.statusText);
  }
  return (await resp.json()) as T;
}

// ---------- Notices ----------

export interface NoticesQuery {
  state?: string;
  employer?: string;
  closure_category?: string;
  industry?: string;
  subsector?: string;
  min_layoffs?: number;
  after?: string;
  before?: string;
  geocoded_only?: boolean;
  // Map box — sent together (a partial box is ignored); implies geocoded.
  min_lat?: number;
  min_lon?: number;
  max_lat?: number;
  max_lon?: number;
  sort_by?: string;
  sort_dir?: string;
  limit?: number;
  offset?: number;
}

export interface RadarQuery {
  state?: string;
  closure_category?: string;
  industry?: string;
  subsector?: string;
  min_layoffs?: number;
  days?: number;
  limit?: number;
  offset?: number;
}

/** /api/map-places: one entry per distinct geocoded point, as parallel
 *  arrays (busiest place first). `workers` sums reported layoff counts. */
export interface MapPlaces {
  lat: number[];
  lon: number[];
  notices: number[];
  workers: number[];
  label: string[]; // "City, ST", or "ST" when the point has no city
}

export interface MapPlacesQuery {
  state?: string;
  closure_category?: string;
  industry?: string;
  subsector?: string;
  min_layoffs?: number;
  after?: string;
  before?: string;
}

export type TimelineBucket = "year" | "quarter" | "month";

/** /api/map-places/timeline: places as in MapPlaces, plus sparse
 *  (place, period) cells as parallel arrays indexing into them. */
export interface MapPlacesTimeline {
  periods: string[]; // "2020", "2020-Q2" or "2020-04"; contiguous, oldest first
  lat: number[];
  lon: number[];
  label: string[];
  place: number[]; // index into lat/lon/label
  period: number[]; // index into periods
  notices: number[];
  workers: number[];
}

/** /api/stats/industries/timeline: per-sector counts aligned with `periods`.
 *  sector "unclassified" holds notices with no known industry. */
export interface IndustryTimeline {
  periods: string[];
  sectors: { sector: string; name: string; notices: number[]; workers: number[] }[];
}

export const api = {
  // ---------- Auth ----------
  login: (email: string, password: string) =>
    post<AuthUser>("/api/auth/login", { email, password }),
  logout: () => post<{ status: string }>("/api/auth/logout"),
  me: () => get<AuthUser>("/api/auth/me"),
  signup: (email: string, password: string) =>
    post<{ status: string; message: string }>("/api/auth/signup", { email, password }),
  forgotPassword: (email: string) =>
    post<{ status: string; message: string }>("/api/auth/forgot", { email }),

  // ---------- API keys / usage (account page) ----------
  listKeys: () => get<ApiKeyOut[]>("/api/keys"),
  createKey: (name?: string) => post<ApiKeyCreatedOut>("/api/keys", { name: name || null }),
  revokeKey: (id: number) => del<{ status: string }>(`/api/keys/${id}`),
  usage: () => get<UsageOut>("/api/usage"),

  listNotices: (q: NoticesQuery = {}) =>
    get<Page<NoticeOut>>("/api/notices" + qs(q as Record<string, string | number | undefined>)),
  getNotice: (id: string) =>
    get<NoticeOut>(`/api/notices/${encodeURIComponent(id)}`),

  // ---------- Radar (upcoming layoffs by effective date) ----------
  listRadar: (q: RadarQuery = {}) =>
    get<Page<RadarNoticeOut>>(
      "/api/radar" + qs(q as Record<string, string | number | undefined>),
    ),
  /** Full estimated occupation mix for one notice (list rows carry the top 3). */
  getOccupationMix: (id: string) =>
    get<OccupationMixOut>(`/api/notices/${encodeURIComponent(id)}/occupation-mix`),

  /** Every geocoded notice matching the filters, aggregated per place — the /map payload. */
  listMapPlaces: (q: MapPlacesQuery = {}) =>
    get<MapPlaces>("/api/map-places" + qs(q as Record<string, string | number | undefined>)),
  /** Per-place counts for every period — /map's timeline, in one payload. */
  mapPlacesTimeline: ({ bucket, ...q }: MapPlacesQuery & { bucket: TimelineBucket }) =>
    get<MapPlacesTimeline>(
      "/api/map-places/timeline" +
        qs({ bucket, ...(q as Record<string, string | number | undefined>) }),
    ),

  // ---------- Companies ----------
  listCompanies: (q: {
    name?: string;
    include_merged?: boolean;
    enriched?: boolean;
    has_unique_id?: boolean;
    sic_code?: string;
    industry?: string;
    subsector?: string;
    sort_by?: string;
    sort_dir?: string;
    limit?: number;
    offset?: number;
  } = {}) =>
    get<Page<CompanyOut>>(
      "/api/companies" +
        qs({
          name: q.name,
          include_merged: q.include_merged ? "true" : undefined,
          enriched: q.enriched === undefined ? undefined : String(q.enriched),
          has_unique_id: q.has_unique_id === undefined ? undefined : String(q.has_unique_id),
          sic_code: q.sic_code,
          industry: q.industry,
          subsector: q.subsector,
          sort_by: q.sort_by,
          sort_dir: q.sort_dir,
          limit: q.limit,
          offset: q.offset,
        }),
    ),
  getCompany: (id: number) => get<CompanyOut>(`/api/companies/${id}`),
  listCompanyNotices: (id: number, q: { limit?: number; offset?: number } = {}) =>
    get<Page<NoticeOut>>(`/api/companies/${id}/notices` + qs(q)),
  /** Repeat-filing risk for the company's latest open filing (404 while dark). */
  getCompanyRepeatRisk: (id: number) =>
    get<RepeatRiskOut>(`/api/companies/${id}/repeat-risk`),
  /** Totals + per-year / per-state rollups for the company page. */
  getCompanySummary: (id: number) =>
    get<CompanySummaryOut>(`/api/companies/${id}/summary`),
  /** Sibling companies sharing this company's corporate family (empty if none). */
  getCompanyFamily: (id: number) =>
    get<FamilyMemberOut[]>(`/api/companies/${id}/family`),
  /** Company rows consolidated into this one (duplicates / store variants). */
  getCompanyMembers: (id: number) =>
    get<MergedMemberOut[]>(`/api/companies/${id}/members`),

  // ---------- Admin (require an admin session) ----------
  adminMergeCompanies: (body: { target_id: number; source_ids: number[]; note?: string }) =>
    post<MergeResult>("/api/admin/companies/merge", body),
  adminUnmergeCompany: (id: number) =>
    post<MergeResult>(`/api/admin/companies/${id}/unmerge`),
  adminMergeSuggestions: (status: "open" | "accepted" | "rejected" = "open") =>
    get<MergeSuggestionOut[]>("/api/admin/companies/merge-suggestions" + qs({ status })),
  adminAcceptSuggestion: (id: number) =>
    post<MergeResult>(`/api/admin/companies/merge-suggestions/${id}/accept`),
  adminRejectSuggestion: (id: number) =>
    post<{ id: number; status: string }>(`/api/admin/companies/merge-suggestions/${id}/reject`),

  // ---------- Scraper runs ----------
  listRuns: (q: { state?: string; status?: string; limit?: number; offset?: number } = {}) =>
    get<Page<ScraperRunOut>>("/api/scraper-runs" + qs(q)),

  /** Per-state scraper health (latest run + last success) for the status page. */
  listScraperStatus: () => get<StateStatusOut[]>("/api/scraper-runs/status"),

  // ---------- Stats ----------
  statsByState: (
    q: {
      closure_category?: string;
      industry?: string;
      subsector?: string;
      after?: string;
      before?: string;
    } = {},
  ) => get<StateStat[]>("/api/stats/by-state" + qs(q)),
  statsByMonth: (
    q: {
      state?: string;
      closure_category?: string;
      industry?: string;
      subsector?: string;
      after?: string;
      before?: string;
    } = {},
  ) => get<MonthStat[]>("/api/stats/by-month" + qs(q)),
  statsOverTime: (
    q: {
      bucket?: "day" | "month" | "year";
      state?: string;
      closure_category?: string;
      industry?: string;
      subsector?: string;
      after?: string;
      before?: string;
    } = {},
  ) => get<PeriodStat[]>("/api/stats/over-time" + qs(q)),
  statsIndicators: (
    q: {
      dataset: IndicatorDataset;
      state?: string;
      industry?: string;
      bucket?: "month" | "year";
      after?: string;
      before?: string;
    },
  ) => get<IndicatorStat[]>("/api/stats/indicators" + qs(q)),
  statsTopEmployers: (
    q: {
      limit?: number;
      state?: string;
      closure_category?: string;
      industry?: string;
      subsector?: string;
      after?: string;
      before?: string;
    } = {},
  ) => get<EmployerStat[]>("/api/stats/top-employers" + qs(q)),
  statsCountyImpact: (
    q: {
      limit?: number;
      min_layoffs?: number;
      state?: string;
      closure_category?: string;
      industry?: string;
      subsector?: string;
      after?: string;
      before?: string;
    } = {},
  ) => get<CountyImpactStat[]>("/api/stats/county-impact" + qs(q)),
  statsByParentGroup: (
    q: { limit?: number; state?: string; after?: string; before?: string } = {},
  ) => get<ParentGroupStat[]>("/api/stats/by-parent-group" + qs(q)),
  statsIndustries: (
    q: {
      state?: string;
      closure_category?: string;
      after?: string;
      before?: string;
    } = {},
  ) => get<IndustryStat[]>("/api/stats/industries" + qs(q)),
  statsIndustriesTimeline: (q: {
    bucket: TimelineBucket;
    state?: string;
    closure_category?: string;
    after?: string;
    before?: string;
  }) => get<IndustryTimeline>("/api/stats/industries/timeline" + qs(q)),

  // ---------- Sentiment reports ----------
  /** One state's latest sentiment report as raw markdown (404 if none).
   *  "US" returns the national roll-up. */
  getReport: (state: string) =>
    getText(`/api/reports/${encodeURIComponent(state)}`),
  /** Scorecard summary for every NAICS sector; [] until the weekly job runs. */
  listIndustryScorecards: () =>
    get<IndustryScorecard[]>("/api/reports/industries"),
  /** One sector's scorecard as raw markdown (404 if none). */
  getIndustryReport: (sector: string) =>
    getText(`/api/reports/industries/${encodeURIComponent(sector)}`),
  /** One jurisdiction's 6-month forecast (404 until the weekly job has run,
   *  or if its history never cleared the lowest forecast model tier).
   *  "US" returns the national forecast. */
  getForecast: (state: string) =>
    get<ForecastOut>(`/api/reports/forecasts/${encodeURIComponent(state)}`),
  /** Trend tests, the unemployment link and gated claims (404 until the
   *  weekly job has run). */
  getOutlook: () => get<Outlook>("/api/reports/outlook"),

  // ---------- Search ----------
  search: (q: string, limit = 8) =>
    get<SearchResults>("/api/search" + qs({ q, limit })),

  // ---------- Alert subscriptions ----------
  createSubscription: (body: { email: string } & AlertFilters) =>
    post<{ status: string; message: string }>("/api/subscriptions", body),

  /** Ask for the emailed management link. Answers the same for an address with
   *  no alerts, so it never reveals whether someone is subscribed. */
  requestManageLink: (email: string) =>
    post<{ status: string; message: string }>("/api/subscriptions/manage-link", { email }),

  // The manage endpoints authenticate with either the emailed token or the
  // session cookie; pass the token when the page was opened from an email.
  listSubscriptions: (token?: string) =>
    get<SubscriptionOut[]>("/api/subscriptions/manage" + qs({ token })),
  /** Replaces the alert's whole filter set — send every field, not a patch. */
  updateSubscription: (id: number, filters: AlertFilters, token?: string) =>
    put<SubscriptionOut>(`/api/subscriptions/manage/${id}` + qs({ token }), filters),
  deleteSubscription: (id: number, token?: string) =>
    del<{ status: string }>(`/api/subscriptions/manage/${id}` + qs({ token })),
  resendConfirmation: (id: number, token?: string) =>
    post<{ status: string }>(
      `/api/subscriptions/manage/${id}/resend-confirmation` + qs({ token }),
    ),
};
